// projectlab-web/src/App.tsx
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { PageLoader } from "@/components/ui";
import { useAuth } from "@/hooks/useAuth";

const LoginPage = lazy(() => import("@/pages/public/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/public/RegisterPage"));
const OwnerRegisterPage = lazy(() => import("@/pages/public/OwnerRegisterPage"));
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const AuthConfirmPage = lazy(() => import("@/pages/public/AuthConfirmPage"));
const ProjectDetailPage = lazy(() => import("@/pages/public/ProjectDetailPage"));

// Talent
const TalentLayout = lazy(() => import("@/pages/talent/TalentLayout"));
const TalentHomePage = lazy(() => import("@/pages/talent/TalentHomePage"));
const TalentExplorePage = lazy(() => import("@/pages/talent/TalentExplorePage"));
const TalentProfile = lazy(() => import("@/pages/talent/TalentProfile"));
const TalentSettings = lazy(() => import("@/pages/talent/TalentSettings"));
const ApplicationsPage = lazy(() => import("@/pages/talent/ApplicationsPage"));
const TalentTeams = lazy(() => import("@/pages/talent/TalentTeams"));
const TalentInterviews = lazy(() => import("@/pages/talent/TalentInterviews"));
const TalentPayments = lazy(() => import("@/pages/talent/TalentPayments"));
const ApplyPage = lazy(() => import("@/pages/talent/ApplyPage"));
const TeamBuilderPage = lazy(() => import("@/pages/talent/TeamBuilderPage"));

// Owner
const OwnerLayout = lazy(() => import("@/pages/owner/OwnerLayout"));
const OwnerOnboardingPage = lazy(() => import("@/pages/owner/OwnerOnboardingPage"));
const OwnerProjects = lazy(() => import("@/pages/owner/OwnerProjects"));
const OwnerProjectDetail = lazy(() => import("@/pages/owner/OwnerProjectDetail"));
const OwnerCreateProject = lazy(() => import("@/pages/owner/OwnerCreateProject"));
const OwnerApplicants = lazy(() => import("@/pages/owner/OwnerApplicants"));
const OwnerInterviews = lazy(() => import("@/pages/owner/OwnerInterviews"));
const OwnerPayments = lazy(() => import("@/pages/owner/OwnerPayments"));
const OwnerSettings = lazy(() => import("@/pages/owner/OwnerSettings"));
const InterviewRoom = lazy(() => import("@/pages/owner/InterviewRoom"));

export default function App() {
  const { loading } = useAuth();

  if (loading) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/owner-register" element={<OwnerRegisterPage />} />
          <Route path="/ownerregister" element={<Navigate to="/owner-register" replace />} />
          <Route path="/auth/confirm" element={<AuthConfirmPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
        </Route>

        {/* Talent onboarding (no profile row required yet) */}
        <Route element={<ProtectedRoute allowedRoles={["talent"]} isOnboarding />}>
          <Route path="/talent/onboarding" element={<TalentHomePage />} />
        </Route>

        {/* Owner onboarding (no profile row required yet) — seeds then redirects */}
        <Route element={<ProtectedRoute allowedRoles={["owner"]} isOnboarding />}>
          <Route path="/owner/onboarding" element={<OwnerOnboardingPage />} />
        </Route>

        {/* Talent (requires profile) */}
        <Route element={<ProtectedRoute allowedRoles={["talent"]} />}>
          <Route element={<TalentLayout />}>
            <Route path="/talent" element={<TalentHomePage />} />
            <Route path="/talent/explore" element={<TalentExplorePage />} />
            <Route path="/talent/profile" element={<TalentProfile />} />
            <Route path="/talent/settings" element={<TalentSettings />} />
            <Route path="/talent/applications" element={<ApplicationsPage />} />
            <Route path="/talent/teams" element={<TalentTeams />} />
            <Route path="/talent/teams/build" element={<TeamBuilderPage />} />
            <Route path="/talent/interviews" element={<TalentInterviews />} />
            <Route path="/talent/payments" element={<TalentPayments />} />
            <Route path="/talent/apply/:projectId" element={<ApplyPage />} />
          </Route>
        </Route>

        {/* Owner (requires profile) */}
        <Route element={<ProtectedRoute allowedRoles={["owner"]} />}>
          <Route element={<OwnerLayout />}>
            <Route path="/owner" element={<OwnerOnboardingPage />} />
            <Route path="/owner/projects" element={<OwnerProjects />} />
            <Route path="/owner/projects/new" element={<OwnerCreateProject />} />
            <Route path="/owner/projects/:id" element={<OwnerProjectDetail />} />
            <Route path="/owner/projects/:id/applicants" element={<OwnerApplicants />} />
            <Route path="/owner/interviews" element={<OwnerInterviews />} />
            <Route path="/owner/payments" element={<OwnerPayments />} />
            <Route path="/owner/settings" element={<OwnerSettings />} />
            <Route path="/interview-room/:roomId" element={<InterviewRoom />} />
          </Route>
        </Route>

        <Route path="/explore" element={<Navigate to="/talent/explore" replace />} />
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}